import identity from 'lodash/identity';
import noop from 'lodash/noop';
import { logErrorWithOnlineCheck, withoutEmptyProps, RequestError, serialize, request, assert } from '..';
import mapSentryEvent from './mapSentryEvent';
const encode = content => window.btoa ? window.btoa(content) : content;

/**
 * Creates opinionated API client
 * that operates over conventional BE response of form:
 * ```{
 *      success: boolean,
 *      content: unknown,
 *      error: { errorCode: string, message: string, errorDetails: unknown }
 * }```
 */
export default function createHttpRequest({
  apiBase,
  options: defaultOptions,
  errorInterceptor = logErrorWithOnlineCheck,
  mapErrorCode = identity,
  captureEvent = noop,
  onBeforeRequest,
  onUnauthorized,
  onSuccess,
  onError
}) {
  function responseInterceptor(response, method) {
    if (response.status === 401 || response.status === 403) {
      onUnauthorized == null ? void 0 : onUnauthorized(response);
      throw new Error('Unauthorized');
    }
    if (response.status >= 500 || response.status === 404) {
      onError == null ? void 0 : onError(response);
    }
    if (response.headers.get('Content-Type') === 'application/octet-stream') {
      return response.blob().then(blob => {
        onSuccess == null ? void 0 : onSuccess(response);
        return blob;
      }).catch(error => {
        throw new Error(error.message || error);
      });
    }
    return response.text().then(textResponse => {
      if (textResponse === '{}' || textResponse === 'null') {
        throw new Error('Empty server response');
      }
      if (response.ok && method === 'HEAD') {
        return true;
      }
      let jsonResponse;
      try {
        jsonResponse = JSON.parse(textResponse);
      } catch (_unused) {
        throw new Error(`Can't parse server response: ${encode(textResponse)}`);
      }
      const {
        success,
        error,
        content
      } = jsonResponse;
      if (success === true) {
        onSuccess == null ? void 0 : onSuccess(response);
        return content;
      }
      if (error != null && error.errorCode) {
        var _error$errorDetails;
        captureEvent(mapSentryEvent(`Error code: ${error.errorCode}`, `Error code: ${error.errorCode}, ${JSON.stringify((_error$errorDetails = error.errorDetails) == null ? void 0 : _error$errorDetails.description)}`));
        throw new RequestError(mapErrorCode(error.errorCode), error.errorDetails, error.message);
      } else if (error) {
        captureEvent(mapSentryEvent(error.message || error));
        throw new Error(error.message || error);
      } else {
        captureEvent(mapSentryEvent(`Bad server response: ${encode(textResponse)}`));
        throw new Error(`Bad server response: ${encode(textResponse)}`);
      }
    });
  }
  const httpRequest = httpMethod => (endpoint, data, localOptions, dtoMapper = withoutEmptyProps) => {
    var _defaultOptions$heade, _localOptions$headers, _updatedRequestData$u, _updatedRequestData$o;
    assert(!!endpoint, '(api/httpRequest): Expected a valid endpoint');
    assert(endpoint.startsWith('/'), '(api/httpRequest): Expected endpoint to start with /');
    const url = `${apiBase}${endpoint}`;
    const options = {
      credentials: 'include',
      method: httpMethod,
      ...(defaultOptions != null ? defaultOptions : {}),
      ...(localOptions != null ? localOptions : {}),
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        Accept: '*/*',
        ...((_defaultOptions$heade = defaultOptions == null ? void 0 : defaultOptions.headers) != null ? _defaultOptions$heade : {}),
        ...((_localOptions$headers = localOptions == null ? void 0 : localOptions.headers) != null ? _localOptions$headers : {})
      }
    };
    if (data instanceof FormData) {
      var _options$headers;
      options.body = data;
      // Browser will handle content type itself in case of multipart form data
      // We can't set multipart by ourself because browser must set multipart boundary
      // @ts-ignore
      (_options$headers = options.headers) == null ? true : delete _options$headers['Content-Type'];
    } else if (data) {
      options.body = JSON.stringify(dtoMapper(data));
    }
    const updatedRequestData = onBeforeRequest == null ? void 0 : onBeforeRequest(url, options);
    return request((_updatedRequestData$u = updatedRequestData == null ? void 0 : updatedRequestData.url) != null ? _updatedRequestData$u : url, (_updatedRequestData$o = updatedRequestData == null ? void 0 : updatedRequestData.options) != null ? _updatedRequestData$o : options, responseInterceptor, errorInterceptor);
  };
  const makeGetRequest = httpMethod => (endpoint, data, localOptions) => httpRequest(httpMethod)(`${endpoint}${serialize(data)}`, null, localOptions);
  return {
    head: makeGetRequest('HEAD'),
    get: makeGetRequest('GET'),
    delete: httpRequest('DELETE'),
    patch: httpRequest('PATCH'),
    post: httpRequest('POST'),
    put: httpRequest('PUT')
  };
}